"use client";

import {
  IconArchive,
  IconBell,
  IconBox,
  IconBoxMultiple,
  IconBrain,
  IconBrandFacebook,
  IconBrandGithubCopilot,
  IconBrandInstagram,
  IconBrandOpenai,
  IconBrandTiktok,
  IconBrandWhatsapp,
  IconBrandX,
  IconBuilding,
  IconBuildingCommunity,
  IconBuildingStore,
  IconBulb,
  IconCalendar,
  IconCalendarEvent,
  IconCash,
  IconChartBar,
  IconChartLine,
  IconChevronRight,
  IconClipboardList,
  IconConfetti,
  IconDashboard,
  IconFileText,
  IconFolder,
  IconFolderOpen,
  IconGift,
  IconInbox,
  IconLayoutDashboard,
  IconLink,
  IconListDetails,
  IconMail,
  IconPackage,
  IconPackages,
  IconReportAnalytics,
  IconRobot,
  IconShare2,
  IconSpeakerphone,
  IconTableOptions,
  IconTruckDelivery,
  IconUmbrella,
  IconUserCircle,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
  IconWorldWww,
} from "@tabler/icons-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";

// Map icon string to React component
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  IconDashboard,
  IconListDetails,
  IconChartBar,
  IconFolder,
  IconUsers,
  IconFolderOpen,
  IconArchive,
  IconChartLine,
  IconReportAnalytics,
  IconBrain,
  IconBuilding,
  IconBuildingCommunity,
  IconBulb,
  IconCash,
  IconCalendar,
  IconLayoutDashboard,
  IconConfetti,
  IconBrandGithubCopilot,
  IconTableOptions,
  IconGift,
  IconBox,
  IconTruckDelivery,
  IconBuildingStore,
  IconPackage,
  IconClipboardList,
  IconLink,
  IconUsersGroup,
  IconUserCircle,
  IconUmbrella,
  IconCalendarEvent,
  IconFileText,
  IconSpeakerphone,
  IconMail,
  IconBrandWhatsapp,
  IconShare2,
  IconBell,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandX,
  IconBrandTiktok,
  IconRobot,
  IconWorldWww,
  IconPackages,
  IconBrandOpenai,
  IconBoxMultiple,
  IconUserPlus,
  IconInbox,
};

interface NavItem {
  title: string;
  url: string;
  icon?: string;
  isActive?: boolean;
  badge?: string;
  items?: NavItem[];
}

export function NavMain({
  items,
  userAccountType,
}: {
  items: NavItem[];
  userAccountType?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();

  // Track which single menu is expanded (accordion behavior)
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Expand the parent menu if the current route matches any of its children
  useEffect(() => {
    const activeParent = items.find(
      (item) =>
        item.items?.some((child) => pathname.startsWith(child.url)) ||
        (item.url && pathname === item.url),
    );

    if (activeParent) {
      setOpenMenu(activeParent.title);
    }
  }, [pathname, items]);

  const handleToggle = (title: string) => {
    setOpenMenu((prev) => (prev === title ? null : title));
  };

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items
            .filter((item) => item.isActive !== false)
            .filter((item) => {
              if (item.title === "Domain Management") {
                if (typeof window === "undefined") return false;
                const host = window.location.hostname;
                const isDev =
                  host === "localhost" || host === "dev.cashlessai.cloud";
                // Dev/localhost: visible to everyone; Prod: admin only
                return isDev || userAccountType === "admin";
              }
              return true;
            })
            .map((item) => {
              const IconComponent = item.icon ? iconMap[item.icon] : null;
              const isActiveRoute = item.url ? pathname === item.url : false;
              const hasChildren = item.items && item.items.length > 0;
              const isOpen = openMenu === item.title;

              if (hasChildren) {
                return (
                  <Collapsible
                    key={item.title}
                    asChild
                    open={isOpen}
                    onOpenChange={() => handleToggle(item.title)}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={isActiveRoute}
                        onClick={(e) => {
                          e.preventDefault();
                          if (item.url) {
                            router.push(item.url);
                          }
                          handleToggle(item.title);
                        }}
                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                      >
                        {IconComponent && <IconComponent />}
                        <span>{item.title}</span>
                        {item.badge && (
                          <Badge
                            variant="secondary"
                            className="ml-auto text-xs"
                          >
                            {item.badge}
                          </Badge>
                        )}
                        <CollapsibleTrigger asChild>
                          <span className="sidebar-caret ml-2 cursor-pointer">
                            <IconChevronRight
                              className={`h-4 w-4 transition-transform duration-200 ${
                                isOpen ? "rotate-90" : ""
                              }`}
                            />
                          </span>
                        </CollapsibleTrigger>
                      </SidebarMenuButton>

                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items?.map((subItem) => {
                            const SubIconComponent = subItem.icon
                              ? iconMap[subItem.icon]
                              : null;
                            const isActiveSubRoute = pathname === subItem.url;
                            const isAIAgent = subItem.title === "AI Agent";

                            return (
                              <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActiveSubRoute}
                                  // className={
                                  //   isAIAgent
                                  //     ? "border-2 border-dotted border-primary/90"
                                  //     : ""
                                  // }
                                >
                                  <Link href={subItem.url} className="group">
                                    {SubIconComponent && (
                                      <SubIconComponent
                                        className={
                                          isAIAgent
                                            ? " h-4 w-4 animate-pulse"
                                            : "h-4 w-4"
                                        }
                                      />
                                    )}
                                    <span>{subItem.title}</span>
                                    {subItem.badge && (
                                      <Badge
                                        variant="secondary"
                                        className="ml-auto text-xs"
                                      >
                                        {subItem.badge}
                                      </Badge>
                                    )}
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              }

              // Simple link (no children)
              if (item.url) {
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      isActive={isActiveRoute}
                    >
                      <Link href={item.url}>
                        {IconComponent && <IconComponent />}
                        <span>{item.title}</span>
                        {item.badge && (
                          <Badge
                            variant="secondary"
                            className="ml-auto text-xs"
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }

              // Return null for items without URL and no children
              return null;
            })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
